"use client"

import { CssBaseline, StyledEngineProvider } from "@mui/material"
import createCache from "@emotion/cache"
import { CacheProvider } from "@emotion/react"
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles"
import rtlPlugin from "stylis-plugin-rtl"
import { useServerInsertedHTML } from "next/navigation"
import React from "react"
import { sharedTheme } from "./theme"
import type { CmsThemeData } from "utils"

const FONT_FAMILY_MAP: Record<string, string> = {
  Rag: "var(--rag), sans-serif",
  Assistant: "'Assistant', sans-serif",
  Heebo: "'Heebo', sans-serif",
  "Open Sans": "'Open Sans', sans-serif",
  "Noto Sans": "'Noto Sans Hebrew', 'Noto Sans', sans-serif",
}

const GOOGLE_FONT_URLS: Record<string, string> = {
  Assistant:
    "https://fonts.googleapis.com/css2?family=Assistant:wght@200;300;400;500;600;700;800&display=swap",
  Heebo:
    "https://fonts.googleapis.com/css2?family=Heebo:wght@100;200;300;400;500;600;700;800;900&display=swap",
  "Open Sans":
    "https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700;800&display=swap",
  "Noto Sans":
    "https://fonts.googleapis.com/css2?family=Noto+Sans+Hebrew:wght@100;200;300;400;500;600;700;800;900&display=swap",
}

function buildCssVariables(themeData: CmsThemeData): string {
  const vars: string[] = []

  themeData.packages.forEach((pkg, i) => {
    const n = i + 1
    vars.push(`--pkg${n}-bg: ${pkg.backgroundColor}`)
    vars.push(`--pkg${n}-text: ${pkg.textColor}`)
    vars.push(`--pkg${n}-heading: ${pkg.headingColor}`)
  })

  const fontFamily = FONT_FAMILY_MAP[themeData.font] || FONT_FAMILY_MAP["Rag"]
  vars.push(`--mlp-font: ${fontFamily}`)

  return vars.map((v) => `${v};`).join("\n    ")
}

const DynamicThemeProvider = ({
  children,
  locale = "he",
  themeData,
}: {
  children: React.ReactNode
  locale: string
  themeData: CmsThemeData
}) => {
  const isRtl = locale === "he"
  const direction = isRtl ? "rtl" : "ltr"

  const theme = React.useMemo(
    () =>
      createTheme({
        ...sharedTheme,
        direction,
        typography: {
          ...sharedTheme.typography,
          fontFamily: FONT_FAMILY_MAP[themeData.font] || FONT_FAMILY_MAP["Rag"],
        },
      }),
    [themeData.font, direction],
  )

  const cssVariables = React.useMemo(() => buildCssVariables(themeData), [themeData])

  const [{ cache, flush }] = React.useState(() => {
    const cache = createCache({
      key: isRtl ? "mlp-rtl" : "mlp-ltr",
      stylisPlugins: isRtl ? [rtlPlugin] : undefined,
    })
    cache.compat = true
    const prevInsert = cache.insert
    let inserted: string[] = []
    cache.insert = (...args) => {
      const serialized = args[1]
      if (cache.inserted[serialized.name] === undefined) {
        inserted.push(serialized.name)
      }
      return prevInsert(...args)
    }
    const flush = () => {
      const prevInserted = inserted
      inserted = []
      return prevInserted
    }
    return { cache, flush }
  })

  useServerInsertedHTML(() => {
    const names = flush()
    if (names.length === 0) {
      return null
    }
    let styles = ""
    for (const name of names) {
      styles += cache.inserted[name]
    }
    return (
      <style
        key={cache.key}
        data-emotion={`${cache.key} ${names.join(" ")}`}
        dangerouslySetInnerHTML={{
          __html: styles,
        }}
      />
    )
  })

  return (
    <StyledEngineProvider injectFirst>
      <CacheProvider value={cache}>
        <MuiThemeProvider theme={theme}>
          <CssBaseline />
          {GOOGLE_FONT_URLS[themeData.font] && (
            <link rel="stylesheet" href={GOOGLE_FONT_URLS[themeData.font]} />
          )}
          <style
            dangerouslySetInnerHTML={{
              __html: `:root { ${cssVariables} }`,
            }}
          />
          {children}
        </MuiThemeProvider>
      </CacheProvider>
    </StyledEngineProvider>
  )
}

export default DynamicThemeProvider
